import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, X, Check } from 'lucide-react';

interface Option {
  value: string;
  label: string;
}

interface MultiSelectDropdownProps {
  value: string[];
  onChange: (values: string[]) => void;
  options: Option[];
  placeholder?: string;
  className?: string;
  pinnedValues?: string[];
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  value,
  onChange,
  options,
  placeholder = 'Select languages',
  className = '',
  pinnedValues = []
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; width: number } | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLDivElement>(null);

  const selectedOptions = options.filter(opt => value.includes(opt.value));

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node) &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const updatePosition = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
          width: rect.width
        });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen]);

  const handleToggleOption = (optionValue: string) => {
    if (pinnedValues.includes(optionValue) && value.includes(optionValue)) return;
    if (value.includes(optionValue)) {
      onChange(value.filter(v => v !== optionValue));
    } else {
      onChange([...value, optionValue]);
    }
  };

  const handleRemove = (optionValue: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (pinnedValues.includes(optionValue)) return;
    onChange(value.filter(v => v !== optionValue));
  };

  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 8,
        left: rect.left,
        width: rect.width
      });
    }
    setIsOpen(!isOpen);
  };

  return (
    <>
      <div ref={containerRef} className={`relative ${className}`}>
        <div
          ref={buttonRef}
          role="button"
          tabIndex={0}
          onClick={handleToggle}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggle(); } }}
          className="w-full min-h-[42px] px-3 py-1.5 bg-gradient-to-br from-white to-gray-50 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-[#1875FF]/50 focus:border-[#1875FF] text-sm cursor-pointer transition-all hover:border-[#1875FF]/40 hover:shadow-lg hover:shadow-[#1875FF]/10 focus:shadow-xl focus:shadow-[#1875FF]/20 font-medium text-left flex items-center justify-between gap-2 group"
        >
          <div className="flex-1 flex gap-1.5 items-center min-h-[28px] overflow-hidden">
            {selectedOptions.length > 0 ? (
              <>
                {selectedOptions.slice(0, 2).map(opt => (
                  <span
                    key={opt.value}
                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#1875FF]/10 text-[#1875FF] rounded-md text-xs font-semibold whitespace-nowrap flex-shrink-0"
                  >
                    {opt.label}
                    {!pinnedValues.includes(opt.value) && (
                      <button
                        type="button"
                        onClick={(e) => handleRemove(opt.value, e)}
                        className="hover:bg-[#1875FF]/20 rounded-full p-0.5 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </span>
                ))}
                {selectedOptions.length > 2 && (
                  <span className="inline-flex items-center px-1.5 py-0.5 bg-[#1875FF]/10 text-[#1875FF] rounded-md text-xs font-semibold whitespace-nowrap flex-shrink-0">
                    +{selectedOptions.length - 2}
                  </span>
                )}
              </>
            ) : (
              <span className="text-gray-400">{placeholder}</span>
            )}
          </div>
          <ChevronDown
            className={`w-4 h-4 text-[#1875FF] transition-all duration-300 group-hover:scale-110 flex-shrink-0 ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        </div>
      </div>

      {isOpen && dropdownPosition && createPortal(
        <div
          ref={dropdownRef}
          className="fixed z-[9999] bg-white border-2 border-gray-200 rounded-xl shadow-2xl shadow-[#1875FF]/20 overflow-hidden animate-fadeIn"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
            width: `${dropdownPosition.width}px`
          }}
        >
          <div className="max-h-64 overflow-y-auto custom-scrollbar">
            {options.length > 0 ? (
              options.map((option, index) => {
                const isSelected = value.includes(option.value);
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => handleToggleOption(option.value)}
                    className={`w-full px-4 py-2.5 text-left hover:bg-[#1875FF]/5 transition-colors font-medium text-sm flex items-center justify-between ${
                      isSelected ? 'bg-[#1875FF]/10 text-[#1875FF]' : 'text-gray-900 hover:text-[#1875FF]'
                    } ${index === 0 ? '' : 'border-t border-gray-100'}`}
                  >
                    <span>{option.label}</span>
                    {isSelected && <Check className="w-4 h-4 text-[#1875FF]" />}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-6 text-center text-gray-400 text-sm">
                No options available
              </div>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
};

export default MultiSelectDropdown;
